/**
 * Auth gate — runs once before React mounts.
 *
 * Three responsibilities:
 *   1. Consume the cleic.com handoff if present
 *      (`#access_token=…&refresh_token=…&type=cleic-handoff`) — restore
 *      the session into the local Supabase client + strip the hash so
 *      tokens never appear in browser history.
 *   2. If a session is present and verified, return mount so main.jsx mounts the app.
 *   3. If no usable session exists, return a visible in-app recovery state.
 *
 * Local dev escape hatch: when `import.meta.env.DEV` is true (i.e. `vite
 * dev`) OR `VITE_DEV_BYPASS_AUTH=1` is set, the gate is skipped entirely.
 *
 * Sign-out propagation: after a successful session, we attach a one-shot
 * SIGNED_OUT listener that bounces back to cleic.com — so signing out from
 * cleic.com (or any sibling app) lands the user back at the front door.
 */
import { supabase } from './supabase.js';
import {
  AUTH_HOME_PARAM,
  DEFAULT_AUTH_HOME_ORIGIN,
  buildInitialReturnToUrl,
  buildReturnToUrl,
  parseHandoffRetryCount,
  resolveAuthHomeOrigin,
  shouldInterruptNoSessionAfterHandoff,
} from './auth-continuity.js';

const configuredCleicHome = import.meta.env.VITE_CLEIC_HOME_URL?.trim?.() ?? '';
const CLEIC_HOME = configuredCleicHome || DEFAULT_AUTH_HOME_ORIGIN;

/**
 * 🔴 SACRED-tier protection: Lens's `data.js` + `engine.js` currently embed
 * protected operator-book source state (BALANCES, FAMILY_AP, MORTGAGES, etc.)
 * as primary state. Until Lens is rebuilt as data-driven from the Ledger
 * API (the "demo companies" feature — multiple anonymized books per
 * business model/size/country, queued for proper architectural rework),
 * Lens MUST only render for approved operator identities. Anyone else who
 * somehow lands here gets bounded in the in-app restricted-access page.
 *
 * Allowlist intentionally hardcoded: this is a tier-2 defense (auth gate
 * is tier-1). The entries represent approved operator access while the
 * protected-source Lens preview remains narrow.
 */
const LENS_PRIVATE_ALPHA_ALLOWLIST = new Set([
  'lorenzo.patawaran@gmail.com',
  'patawaran.jl@gmail.com',
  'jcpatawaran@gmail.com',
  'admin@turevibe.com',
  'jl@cleic.com',
]);

function devBypassEnabled() {
  if (import.meta.env.DEV) return true;
  return import.meta.env.VITE_DEV_BYPASS_AUTH === '1';
}

function currentPathWithoutHash() {
  return window.location.pathname + window.location.search;
}

function resolveAuthHome() {
  const candidate = new URLSearchParams(window.location.search).get(AUTH_HOME_PARAM);
  return resolveAuthHomeOrigin(candidate, CLEIC_HOME);
}

function handoffRetryCount() {
  return parseHandoffRetryCount(window.location.search);
}

function currentUrlForReturnTo(status) {
  if (status) {
    return buildReturnToUrl(window.location.origin, currentPathWithoutHash(), status);
  }
  return buildInitialReturnToUrl(window.location.origin, currentPathWithoutHash());
}

function buildLoginUrl(status) {
  const url = new URL(resolveAuthHome());
  url.searchParams.set('return_to', currentUrlForReturnTo(status));
  return url.toString();
}

function clearCurrentHash() {
  window.history.replaceState(null, '', currentPathWithoutHash());
}

function readWindowNameHandoff() {
  if (!window.name) return null;
  try {
    const parsed = JSON.parse(window.name);
    if (parsed?.type !== 'cleic-window-name-handoff-v1') return null;
    if (parsed.targetOrigin !== window.location.origin) return null;
    if (!parsed.createdAt || Date.now() - parsed.createdAt > 2 * 60 * 1000) return null;
    return parsed;
  } catch {
    return null;
  }

  return 'restored';
}

async function consumeHandoffIfPresent() {
  try {
    const hash = window.location.hash;
    const windowNameHandoff = !hash ? readWindowNameHandoff() : null;
    if (!hash && !windowNameHandoff) return 'none';

    const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
    const accessToken = windowNameHandoff?.access_token ?? params.get('access_token');
    const refreshToken = windowNameHandoff?.refresh_token ?? params.get('refresh_token');
    if (!windowNameHandoff && !accessToken && !refreshToken && !hash.includes('type=cleic-handoff')) return 'none';

    // Strip the hash before session restore so failure paths cannot retain tokens.
    window.name = '';
    clearCurrentHash();

    if (!accessToken || !refreshToken) return 'failed';

    const { error } = await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    if (error) {
      // eslint-disable-next-line no-console
      console.warn('[cleic-lens] handoff session restore failed:', error.message);
      return 'failed';
    }

    return 'restored';
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[cleic-lens] handoff session restore crashed:', error?.message || error);
    return 'error';
  }

  return 'restored';
}

function redirectToLogin(status) {
  window.location.replace(buildLoginUrl(status));
}

function authRequiredState(reason = 'session_required', status) {
  return {
    kind: 'auth-required',
    reason,
    authHome: resolveAuthHome(),
    loginUrl: buildLoginUrl(status),
  };
}

function authErrorState(reason = 'auth_bootstrap_failed') {
  return {
    kind: 'auth-error',
    reason,
    authHome: resolveAuthHome(),
    loginUrl: buildLoginUrl('invalid_session'),
  };
}

/**
 * Bootstrap the auth gate. Returns one of three states:
 *   - { kind: 'mount' } → caller should mount the app
 *   - { kind: 'redirect' } → sign-out propagation redirect is in flight; caller MUST not mount
 *   - { kind: 'access-denied', email } → render in-app AccessRestricted page
 *   - { kind: 'auth-required', authHome, loginUrl } → render bounded sign-in recovery
 *   - { kind: 'auth-error', authHome, loginUrl } → render bounded auth/bootstrap recovery
 *   - { kind: 'handoff-interrupted', authHome } → render bounded recovery
 *
 * Tri-state replaces the previous bool to fix the redirect-loop where
 * cleic.com didn't handle ?lens_private_alpha=true and the user's
 * subdomain session cache was still active.
 */
export async function bootstrapAuthGate() {
  if (devBypassEnabled()) return { kind: 'mount' };

  const handoffResult = await consumeHandoffIfPresent();
  if (handoffResult === 'error') {
    return authErrorState('handoff_consume_failed');
  }

  let sessionResult;
  try {
    sessionResult = await supabase.auth.getSession();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[cleic-lens] getSession crashed:', error?.message || error);
    return authErrorState('session_bootstrap_failed');
  }

  const { data, error } = sessionResult;
  if (error) {
    // eslint-disable-next-line no-console
    console.warn('[cleic-lens] getSession error:', error.message);
    return authErrorState('session_bootstrap_error');
  }

  if (data?.session) {
    let userResult;
    try {
      userResult = await supabase.auth.getUser();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[cleic-lens] getUser crashed:', error?.message || error);
      return authErrorState('session_verification_failed');
    }

    const { data: verified, error: verifyError } = userResult;
    if (verifyError || !verified?.user) {
      await supabase.auth.signOut().catch(() => {});
      if (shouldInterruptNoSessionAfterHandoff(window.location.search)) {
        return { kind: 'handoff-interrupted', authHome: resolveAuthHome() };
      }
      return authRequiredState('invalid_session', 'invalid_session');
    }

    // 🔴 Lens protected-preview allowlist gate. Only approved operator
    // identities can see Lens until the demo-companies refactor lands.
    // Others get an in-app
    // AccessRestricted page instead of a cross-origin redirect — the
    // in-app rendering avoids the infinite-loop bug where cleic.com
    // doesn't handle the rejection param.
    const userEmail = (verified.user.email ?? '').toLowerCase();
    if (!LENS_PRIVATE_ALPHA_ALLOWLIST.has(userEmail)) {
      // main.jsx will render <AccessRestricted email={...} />.
      return {
        kind: 'access-denied',
        email: verified.user.email ?? null,
      };
    }

    // Sign-out propagation #1 — local SIGNED_OUT events (same-tab sign-out).
    supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') redirectToLogin();
    });

    // Sign-out propagation #2 — cross-origin detection. cleic.com's Sign Out
    // uses signOut({scope: "global"}) which revokes refresh tokens server-side.
    // We re-validate against the auth API every time the tab regains focus —
    // catches "user signed out on cleic.com tab, switched back to Lens tab"
    // instantly.
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        revalidateOrRedirect();
      }
    });
    window.addEventListener('focus', revalidateOrRedirect);

    return { kind: 'mount' };
  }

  if (handoffResult === 'failed') {
    if (handoffRetryCount() < 1) return authRequiredState('handoff_restore_failed', 'handoff_restore_failed');
    return { kind: 'handoff-interrupted', authHome: resolveAuthHome() };
  }

  if (handoffResult === 'restored' && shouldInterruptNoSessionAfterHandoff(window.location.search)) {
    return { kind: 'handoff-interrupted', authHome: resolveAuthHome() };
  }

  return handoffResult === 'restored'
    ? authRequiredState('no_session_after_handoff', 'no_session_after_handoff')
    : authRequiredState('session_required');
}

async function revalidateOrRedirect() {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data?.user) {
    await supabase.auth.signOut().catch(() => {});
    redirectToLogin();
  }
}
